import { actions } from "@app/utils";
import { createApiClient, getAuthHeaders } from "@app/utils/apiClient";
import { backgroundDelay } from "@app/utils/workerTimer";

export const MAX_CONCURRENT_REQUESTS = 3;

export const photoshopClient = createApiClient(
  actions["exd-storyteller-ai/photoshop"],
  MAX_CONCURRENT_REQUESTS,
);

export const DEFAULT_CROP_OBJECTS = "logo.person";

/**
 * @param {{ items: Array<[url: string, resolutions: Array<{width: number, height: number}>, objects?: string]>}} params
 * `objects` is optional per item and defaults to `logo.person`.
 */
export const cropImage = async ({ items = [] }, options = {}) => {
  const inputs = [];
  const features = [];

  let index = 0;
  for (const [url, resolutions, objects] of items) {
    // const presignedUrl = await fetchPresignURL(filePath);

    inputs.push({
      "dc:format": "image/jpeg",
      "repo:path": url,
      "sensei:repoType": "HTTP",
    });

    const feature = {
      feature_name: `image${index + 1}`,
      feature_value: {
        // aspect_ratios: [],
        // scales: [],
        // preserve_symmetry: true,
        auto: false,
        objects: objects ? ["logo", objects].join(".") : DEFAULT_CROP_OBJECTS,
        target_resolution: [],
      },
    };

    index++;

    for (const { width, height } of resolutions) {
      feature.feature_value.target_resolution.push({ w: width, h: height });
    }

    features.push(feature);
  }

  const payload = {
    "sensei:invocation_batch": false,
    "sensei:engines": [
      {
        "sensei:execution_info": {
          "sensei:engine":
            // "Feature:autocrop:Service-e28a51fdd1274c598a93bbc942ccf449", // V3
            "Feature:autocrop:Service-bab6a9e1e21049bc9aff1cb76652f56d", // V4
        },
        "sensei:inputs": {
          image_input: inputs,
        },
        "sensei:params": {
          autocrop_params: {
            features: features,
          },
        },
        "sensei:outputs": {
          predict: {
            "dc:format": "application/json",
            "sensei:multipart_field_name": "outjson",
          },
        },
      },
    ],
  };

  const formData = new FormData();
  formData.append("contentAnalyzerRequests", JSON.stringify(payload));

  const headers = await getAuthHeaders();

  // const response = await photoshopClient.post("/auto-crop", formData, {
  //   headers,
  //   ...options,
  // });

  const response = await fetch(
    `${actions["exd-storyteller-ai/photoshop"]}/auto-crop`,
    {
      method: "POST",
      body: formData,
      headers, // Let browser set Content-Type with correct boundary
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to crop image: ${response.status}`, {
      cause: response,
    });
  }

  return response.json();
};

/**
 * Start an async background-removal job.
 * @returns {Promise<Object>} `{ jobId, statusUrl }`
 */
export const removeBackground = async (body = {}, options = {}) => {
  const response = await photoshopClient.post(
    "/remove-background",
    body,
    options,
  );

  if (!response.ok) {
    throw new Error(`Failed to start background removal`, {
      cause: response,
    });
  }

  if (!response.data?.statusUrl) {
    throw new Error(`Invalid response format from remove background`, {
      cause: response,
    });
  }

  return response.data;
};

/** Poll a Photoshop v2 job once. */
export const pollRemoveBackgroundStatus = async (
  { statusUrl, s3FilePath } = {},
  options = {},
) =>
  photoshopClient.post(
    "/poll-status",
    {
      statusUrl,
      ...(s3FilePath ? { s3FilePath } : {}),
    },
    options,
  );

/**
 * Run a background removal end to end: start the job, poll until it settles,
 * and normalise the result.
 *
 * Returns the **same shape as `generateImageFn`** (`{ success, outputs: [...] }`)
 * so callers that already consume generated image variations — including the
 * upload/persist path in `ImageVariations` — work with it unchanged.
 */
export const removeBackgroundFn = async (
  options = {},
  signal,
  statusCallback = null,
) => {
  const { s3FilePath, ...removeBgOptions } = options ?? {};
  const { statusUrl } = await removeBackground(removeBgOptions, { signal });

  const maxAttempts = 120; // 2 minutes
  const initialPollDelay = 1500;
  const pollDelay = 1500;

  let attempts = 0;

  await backgroundDelay(initialPollDelay, { signal });

  while (attempts < maxAttempts) {
    attempts++;

    const pollResponse = await pollRemoveBackgroundStatus(
      { statusUrl, s3FilePath },
      { signal },
    );

    if (!pollResponse.ok && pollResponse.status !== 429) {
      throw new Error("Failed to poll background removal status", {
        cause: pollResponse,
      });
    }

    const jobResult = pollResponse.data;

    if (jobResult?.status === "succeeded") {
      const outputs = (jobResult?.result?.outputs ?? [])
        .filter((output) => !!output?.destination?.url)
        .map((output) => {
          const s3Data = output?.s3 ?? jobResult?.s3 ?? null;
          return {
            kind: "image",
            url: s3Data?.presignURL ?? output.destination.url,
            filePath: s3Data?.filePath ?? null,
            presignURL: s3Data?.presignURL ?? null,
            mediaType: output.mediaType,
          };
        });

      if (outputs.length === 0) {
        throw new Error("No valid outputs were found in the response", {
          cause: pollResponse,
        });
      }

      return { success: true, outputs };
    }

    if (jobResult?.status === "failed") {
      const [firstError] = jobResult?.errorDetails ?? [];

      throw new Error(firstError?.message ?? "Background removal job failed", {
        cause: {
          status: pollResponse.status,
          data: {
            status: "failed",
            jobId: jobResult?.jobId,
            error_code: firstError?.errorCode,
            message: firstError?.message,
          },
        },
      });
    }

    statusCallback?.(jobResult ?? {});

    const delay = Math.max(
      pollDelay,
      pollResponse?.headers?.["retry-after"]
        ? parseInt(pollResponse.headers["retry-after"], 10) * 1000
        : 0,
    );

    // Wait before polling again (worker-backed)
    await backgroundDelay(delay, { signal });
  }

  throw new Error(`Job timed out after ${attempts} attempts`);
};
